import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertCircle, KeyRound, LogIn, UserRound } from 'lucide-react';
import { useAuth } from './AuthProvider';
import { LoginShell } from './LoginShell';
import { isMfaPending } from './types';
import { ApiError, errorMessage, isAccessBlockedError } from '@/lib/api';
import { Button } from '@/components/Button';
import { FormField } from '@/components/FormField';
import { TextField } from '@/components/TextField';
import { APP_NAME } from '@/components/PageHeader';

const schema = z.object({
  loginId: z.string().trim().min(1, 'ログインIDを入力してください'),
  password: z.string().min(1, 'パスワードを入力してください'),
});
type FormValues = z.infer<typeof schema>;

/** ログイン後の遷移先（オープンリダイレクト防止のため同一オリジンの相対パスのみ） */
export function safeRedirect(v: string | null): string {
  if (!v || !v.startsWith('/') || v.startsWith('//') || v.startsWith('/login')) return '/';
  return v;
}

/** 2段階認証の画面へ遷移するときの redirect クエリ */
export function redirectQuery(redirect: string): string {
  return redirect && redirect !== '/' ? `?redirect=${encodeURIComponent(redirect)}` : '';
}

export function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const redirect = safeRedirect(params.get('redirect'));
  const [serverError, setServerError] = useState<string | null>(null);
  const [capsLock, setCapsLock] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: { loginId: '', password: '' } });

  useEffect(() => {
    document.title = `ログイン｜${APP_NAME}`;
  }, []);

  if (user) return <Navigate to={redirect} replace />;

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    try {
      const result = await login(values);
      if (isMfaPending(result)) {
        // 2段階認証：確認コード入力 または 認証アプリの登録へ
        navigate(`${result.mfaPending === 'verify' ? '/login/mfa' : '/login/mfa-setup'}${redirectQuery(redirect)}`, { replace: true });
        return;
      }
      navigate(redirect, { replace: true });
    } catch (e) {
      // IP 制限・端末証明書による拒否は画面全体の案内（AccessGate）に切り替わる
      if (isAccessBlockedError(e)) return;
      setServerError(
        e instanceof ApiError && e.status === 401 ? 'ログインIDまたはパスワードが正しくありません。' : errorMessage(e),
      );
    }
  });

  return (
    <LoginShell>
      <h1 className="bs-login__title">ログイン</h1>
      <p className="bs-login__sub">発行されたログインIDとパスワードを入力してください。</p>

      {serverError && (
        <div className="bs-login__alert" role="alert">
          <AlertCircle size={18} aria-hidden />
          <span>{serverError}</span>
        </div>
      )}

      <form onSubmit={onSubmit} noValidate className="bs-login__form">
        <FormField label="ログインID" error={errors.loginId?.message}>
          <TextField
            autoComplete="username"
            autoFocus
            prefix={<UserRound size={16} />}
            placeholder="例）fund-admin"
            {...register('loginId')}
          />
        </FormField>
        <FormField
          label="パスワード"
          error={errors.password?.message}
          hint={capsLock ? 'Caps Lock がオンになっています' : undefined}
        >
          <TextField
            type="password"
            autoComplete="current-password"
            prefix={<KeyRound size={16} />}
            onKeyUp={(e) => setCapsLock(e.getModifierState('CapsLock'))}
            {...register('password')}
          />
        </FormField>
        <Button type="submit" variant="primary" size="lg" block loading={isSubmitting} icon={<LogIn size={18} />}>
          ログイン
        </Button>
      </form>

      <ul className="bs-login__notes">
        <li>本システムはパソコン専用です。スマートフォン・タブレットからはご利用いただけません。</li>
        <li>パスワードを忘れた場合は、振興基金の担当者へお問い合わせください。</li>
      </ul>
    </LoginShell>
  );
}
